import clsx from "clsx";
import React from "react";

import sharedStyles from "../../styles/shared/inputs.module.css";
import styles from "./index.module.css";
import Tooltip from "components/Tooltip";

export interface ToggleGroupOption {
  id: string;
  label?: string;
  icon?: React.ReactNode;
  /** Accessibility label */
  "aria-label"?: string;
}

export interface ToggleGroupProps {
  /** Options */
  options: ToggleGroupOption[];
  /** Currently selected option */
  selectedOption?: ToggleGroupOption;
  /** Callback when selection changes */
  onChange?: (selectedOption: ToggleGroupOption) => void;
  /** Disabled state */
  isDisabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Accessibility label */
  "aria-label"?: string;
}

/**
 * A toggle group component for toggling between multiple options
 */
const ToggleGroup = ({
  options,
  selectedOption,
  onChange,
  isDisabled = false,
  className,
  "aria-label": ariaLabel,
}: ToggleGroupProps) => {
  const currentSelection = selectedOption || options[0];

  const handleClick = (option: ToggleGroupOption) => {
    if (isDisabled) return;
    onChange?.(option);
  };

  const containerClasses = clsx(
    styles.switchContainer,
    {
      [sharedStyles.isDisabled]: isDisabled,
    },
    className
  );

  return (
    <div className={containerClasses}>
      {options.map((option) => {
        const button = (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={currentSelection?.id === option.id}
            aria-label={ariaLabel || "Switch between options"}
            className={clsx(styles.option, {
              [styles.optionActive]: currentSelection?.id === option.id,
            })}
            disabled={isDisabled}
            onClick={() => handleClick(option)}
          >
            {option.icon && option.icon}
            {option.label && option.label}
          </button>
        );

        if (option["aria-label"]) {
          return (
            <Tooltip
              key={`tooltip-${option.id}`}
              content={option["aria-label"]}
            >
              {button}
            </Tooltip>
          );
        }

        return button;
      })}
    </div>
  );
};

ToggleGroup.displayName = "ToggleGroup";
export default ToggleGroup;
